import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BannerImage from '../../assets/nearby-merchants/banner-1.png';
import SingleProductDetails from '../../components/MerchantDetails/SingleProductDetails';
import useCartActions from '../../hooks/useCartActions';
import Toast from '../../components/Toast';
import DOMPurify from 'dompurify';
import { useAppliedFilters } from '../../hooks/useAppliedFilters';
import { useFilterConfig } from '../../contexts/filters';
import { applyProductFilters, collectProductCategories } from '../../utils/filters';

const SearchedProducts = ({ products }) => {
  const { t, i18n } = useTranslation();
  const appliedFilters = useAppliedFilters();
  const { setFilterConfig, resetFilterConfig } = useFilterConfig();

  const [selectedProduct, setSelectedProduct] = useState(null);

  const { addToCart, getMaxVariantFromCart, getVariantQuantitiesFromCart, changeQuantity, toastMessage, toastType } = useCartActions();

  const [variantDecision, setVariantDecision] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const availableCategories = useMemo(
    () => collectProductCategories(products, i18n.language),
    [products, i18n.language],
  );

  useEffect(() => {
    setFilterConfig({ mode: 'products', categories: availableCategories });
    return () => resetFilterConfig();
  }, [availableCategories, setFilterConfig, resetFilterConfig]);

  const displayedProducts = useMemo(
    () => applyProductFilters(products, appliedFilters, i18n.language),
    [products, appliedFilters, i18n.language],
  );

  const openVariantDecisionModal = (data) => {
    setVariantDecision(data);
  };

  const handleVariantQuantityIntent = (product, type) => {
    const { item } = getMaxVariantFromCart(product._id);

    // no variants → normal behavior
    if (!product.variants?.length) {
      changeQuantity(product, type);
      return;
    }

    openVariantDecisionModal({
      product,
      variantItem: item,
      type,
    });
  };

  const handleModifySameVariant = () => {
    if (!variantDecision) {
      return;
    }

    const { product, variantItem, type } = variantDecision;

    changeQuantity(
      {
        ...product,
        variantId: variantItem.variantId,
      },
      type,
    );

    setVariantDecision(null);
  };

  const handleChooseAnotherVariant = () => {
    if (!variantDecision) {
      return;
    }

    setSelectedProduct(variantDecision.product); // opens existing product modal
    setVariantDecision(null);
  };

  return (
    <>
      {displayedProducts.length === 0 ? (
        <div className="px-3 md:px-7 py-6 text-center text-[#b5b5b5]">
          {t('No products found')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-3 md:px-7">
          {displayedProducts.map((product) => (
            <div
              key={product._id}
              className="border p-4 rounded cursor-pointer flex justify-between relative rounded-xl
              transition-all duration-300 ease-out
              hover:border-[#dddddd] hover:shadow-md"
              onClick={() => setSelectedProduct(product)}
            >
              <div className="flex flex-col justify-between pr-4 w-2/3">
                <div>
                  <h3 className="font-semibold text-lg">
                    {i18n.language.startsWith('fr') ? product.frName : product.enName}
                  </h3>
                  <p
                    className="text-sm text-gray-600 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        ((i18n.language.startsWith('fr') ? product.frDescription : product.enDescription) || '')
                          .replace(/\\n/g, '<br />'),
                      ),
                    }}
                  />
                </div>
                <p className="mt-2 font-bold text-base leading-tight">
                  {product?.price !== null ? (
                    <span>{product?.price.toFixed(2)}$</span>
                  ) : (
                    <span className="text-xs text-[#999] leading-tight">
                      {t('noPriceMessage')}
                    </span>
                  )}
                </p>
              </div>

              <div className="w-[120px] h-[120px] rounded-lg border border-[#E1E1E1] flex items-center justify-center flex-shrink-0 relative">
                <div className="w-full h-full rounded-lg overflow-hidden">
                  <img
                    src={product.images?.[0] || BannerImage}
                    alt={product.enName}
                    className={`w-full h-full object-contain transition-all duration-300
                    ${product.availability !== 'in-stock' ? 'filter grayscale brightness-90' : ''}`}
                  />
                  {product.availability !== 'in-stock' && (
                    <div className="absolute top-0 left-0 w-full bg-black bg-opacity-50 py-1 px-2 flex items-center justify-center rounded-t-lg">
                      <span className="text-white text-lg font-semibold text-center">
                        {t('Out of stock')}
                      </span>
                    </div>
                  )}
                </div>

                {product.availability === 'in-stock' && product?.price !== null && product?.price !== undefined && (
                  getMaxVariantFromCart(product._id).quantity > 0 ? (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] flex items-center bg-black text-white border border-black rounded px-2 py-1 text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVariantQuantityIntent(
                            product,
                            'decrease',
                          );
                        }}
                        className="px-2 text-lg"
                      >
                                  −
                      </button>

                      <span className="px-3">
                        {getMaxVariantFromCart(product._id).quantity}
                      </span>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVariantQuantityIntent(
                            product,
                            'increase',
                          );
                        }}
                        className="px-2 text-lg"
                      >
                                  +
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        if (product.variants?.length > 0) {
                          return;
                        }
                        e.stopPropagation();
                        addToCart(product);
                      }}
                      className="absolute left-1/2 -translate-x-1/2 bottom-[-12px] bg-white text-black text-sm font-medium px-5 py-1.5 rounded border border-[#E1E1E1] hover:bg-[#33b0d4] transition duration-300 whitespace-nowrap capitalize"
                    >
                      {t('add')} +
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Toast message={toastMessage} type={toastType} />

      {variantDecision && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end justify-center z-50"
          onClick={() => setVariantDecision(null)}
        >
          <div
            className="
                      bg-white w-full max-w-lg
                      p-6
                      shadow-lg
                      animate-slide-up2
                    "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="-mx-6 -mt-6 mb-4 bg-[#ccc] px-6 py-4">
              <h3 className="text-lg font-semibold leading-tight">
                {i18n.language.startsWith('fr')
                  ? variantDecision.product?.frName
                  : variantDecision.product?.enName}
              </h3>
            </div>

            <p className="text-sm mb-6">
              {t('previousSelections')}:{' '}
              {i18n.language.startsWith('fr')
                ? variantDecision.variantItem?.frVariantName
                : variantDecision.variantItem?.enVariantName}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleChooseAnotherVariant}
                className="flex-1 border border-black bg-white py-3 font-medium hover:bg-gray-100"
              >
                {t('chooseVariant')}
              </button>

              <button
                onClick={handleModifySameVariant}
                className="flex-1 bg-black text-white border border-gray-300 py-3 font-medium"
              >
                {t('repeat')}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <SingleProductDetails
          product={selectedProduct}
          cartQuantities={getVariantQuantitiesFromCart(selectedProduct._id)}
          onClose={() => setSelectedProduct(null)} />
      )}
    </>
  );
};

export default SearchedProducts;
